import { ReactNode, useState } from 'react'

import { toast, ToastContentProps } from 'react-toastify'

import InfoBox from '@/Atomic/Molecules/InfoBox'
import { errorToast } from '@/Components/Toast'
import { useDelayedCallback } from '@/hooks/useDelayedCallback'
import { reportError } from '@/utils/reportError'

import './UndoableActionToast.scss'

interface UndoableActionToastProps extends Partial<ToastContentProps> {
  className?: string
  message: ReactNode
  onCommit: () => Promise<unknown> | unknown
  onError?: () => void
  onUndo?: () => void
  timeoutMs: number
  undoLabel: ReactNode
}

export const UndoableActionToast = ({
  className,
  closeToast,
  message,
  onCommit,
  onError,
  onUndo,
  timeoutMs,
  undoLabel,
}: UndoableActionToastProps) => {
  const [isUndoing, setIsUndoing] = useState(false)
  const { cancel } = useDelayedCallback(timeoutMs, async () => {
    try {
      await onCommit()
    } catch (error) {
      onError?.()
      errorToast(error as Error)
      reportError(error)
    }
  })

  const handleUndo = () => {
    if (isUndoing) return

    setIsUndoing(true)
    cancel()
    onUndo?.()
    closeToast?.()
  }

  return (
    <InfoBox
      className={className}
      type="success"
      onClose={() => closeToast?.()}
    >
      <span>{message}</span>
      <button
        type="button"
        className="undoable-action-toast-undo"
        disabled={isUndoing}
        onClick={handleUndo}
      >
        {undoLabel}
      </button>
    </InfoBox>
  )
}

export const showUndoableActionToast = ({
  timeoutMs = 5000,
  ...props
}: Omit<UndoableActionToastProps, 'closeToast' | 'timeoutMs'> & {
  timeoutMs?: number
}) => {
  toast(<UndoableActionToast {...props} timeoutMs={timeoutMs} />, {
    autoClose: timeoutMs,
    pauseOnFocusLoss: false,
    pauseOnHover: false,
  })
}
